<template>
    <div class="tw-flex tw-flex-col tw-gap-5">
      <!-- Upload Area -->
      <div
        id="document-upload"
        class="tw-flex tw-flex-col tw-gap-1"
      >
        <!-- Select or Upload -->
        <div>
          <label class="heading">Extract Account Onboarding Information</label>
          <label class="heading">Client name, address, and account details</label>
          <div class="tw-w-full tw-flex tw-gap-3 tw-items-center">
                <o-upload v-model="files" multiple>
                  <a>
                    <i class="fa fa-file-pdf tw-mr-1"></i>
                    Upload PDF(s) to extract account onboarding information
                  </a>
                </o-upload>
          </div>
        </div>

        <!-- Upload status -->
        <div
          v-if="isUploading"
          class="tw-flex tw-flex-col tw-gap-2 tw-text-slate-500"
        >
          <div>
            <p>Processing new documents:</p>
          </div>

          <div
            v-for="statementName in Object.keys(taskIDs)"
            :key="statementName"
            class="tw-flex tw-gap-2"
          >
            <div><i :class="statusData[taskIDs[statementName]]?.icon || 'fa fa-spinner fa-spin'" /></div>
            <div class="tw-flex tw-flex-col">
              <p>
                <span class="tw-font-bold tw-whitespace-nowrap">{{ statementName }}</span>
              </p>
              <p class="tw-text-sm">
                {{ statusData[taskIDs[statementName]]?.msg || 'Starting' }}
              </p>
            </div>
          </div>
        </div>
  
        <!-- Download CSV -->
        <!-- <p
          v-else-if="readyToSubmit"
          class="tw-text-sm tw-text-slate-500"
        >
        </p> -->
          <!-- This may look confusing but basically if the "csv" key exists, it means the analytics have completed -->
          <!-- At that point, if the download link above doesn't exist, it means we failed to generate the csv. -->
        
        <!-- Notifications -->
        <div class="tw-max-w-md">
          <!-- Error message when one or more statement uploads fail -->
          <b-notification
            v-if="failedUploads?.length && !isUploading"
            type="is-link"
          >
            <div class="tw-flex tw-flex-col tw-items-center tw-gap-3">
              <p class="tw-max-w-sm">
                Something went wrong while trying to upload and process the documents below. Make sure they are complete brokerage statements. </p>
              <p
                v-for="statement in failedUploads"
                :key="statement"
                class="tw-font-bold tw-max-w-sm"
              >
                {{ statement }}
              </p>
            </div>
          </b-notification>
        </div>
      </div>
  

        <div
          v-if="csvFiles.length"
          class="tw-flex tw-flex-col tw-gap-1"
        >
          <p class="tw-font-semibold tw-text-lg">
            Spreadsheets to download with account onboarding information
          </p>
          <a
            v-for="file in csvFiles"
            v-if="file" :key="file?.filename"
            :href="file?.url"
            class="tw-text-sm"
          >
            Spreadsheet for {{ file?.filename }}
          </a>
        </div>
      </div>

  </template>
  
  
  
  <script>


  // Helpers and other imports
  import { newWorkbook, getWorkbookURL} from '@frontend/shared/exceljs/helpers';
  import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';
  import { postSendInternalEmail } from '@frontend/api/emailsApi.js';
  import * as Sentry from '@sentry/vue';
  
  export default {
    name: 'AccountOnboardingSection',
    components: { },
    props: [],
    data() {
      return {
        files: [],
        url: null,
        csvFile: {},
        taskResults: {},
        isUploading: false,

  
        taskFailed: false,
        failedTasks: [], // This replaces taskFailed which was before multiple uploads
        taskCheckInterval: null,
  
        statusMessage: 'Starting',
        statusData: {},
  
        taskIDs: {},
        portfolioID: null,
  
        csvFiles: [],
        // Results will go here
        results: {
          csv: null,
        },
  
      };
    },
    computed: {
      headers() {
        return {
          'X-CSRFToken': this.$root.$data.csrfToken,
        };
      },
      filename() {
        const name = this.file?.name;
        if (!name) return null;
  
        const limit = 50;
        if (name.length > limit) {
          return name.substring(0, limit) + '...';
        }
        return name;
      },
      activeStatus() {
        const statuses = Object.values(this.statusData);
        if (!statuses.length) return { };
  
        const sorted = statuses.sort((a, b) => a.progress - b.progress);
        return sorted[0];
      },
      failedUploads() {
        return this.failedTasks?.map(task => task.meta.statement_name);
      },
    },
    watch: {
      'files': {
        handler: function(val) {
          console.log('FILES CHANGED');
          if (val?.length) {
            this.startAnalysis();
          }
        },
        deep: true,
      },
      'activeStatus': function() {
        this.statusMessage = this.activeStatus?.msg;
      },
    },
    updated() {
  
    },
    async created() {
      // check for params, if they're there get the check up and call selectBenchmark and selectExistingPortfolio
      const portfolioId = new URLSearchParams(window.location.search).get('portfolio');
      const prospectId = new URLSearchParams(window.location.search).get('prospect_id');
      console.log('portfolioId query param: ', portfolioId);
      console.log('prospectId query param: ', prospectId);

    },
    beforeDestroy() {
      clearInterval(this.taskCheckInterval);
    },
    methods: {

      startAnalysis() {
        this.isUploading = true;
        this.taskFailed = false;
        this.failedTasks = [];
        this.statusMessage = 'Starting';
        this.files.forEach(file => this.sendStatement(file));
  
        // This will check the background task status every 5 seconds
        this.taskCheckInterval = setInterval(() => this.checkTaskStatus(), 2500);
      },
      async sendStatement(file) {
        // Sends the pdf statement to the backend and gets a background task id
        //const url = '/api/v1/process/statement_upload/';
        const url = '/api/v1/process/statement_upload_onboarding/';

        // Prepares the file as form data so it can be sent in the request
        const formData = new FormData();

        formData.append('statement', file);

        if (this.mockStatement) formData.append('mock', this.mockStatement);

        // Send the file
        const res = await fetch(url, {
          method: 'POST',
          headers: this.headers,
          body: formData,
        });

        // Handles a failed upload
        // Among other things, this should catch the 403 errors when a statement is blocked by AWS WAF rules.
        if (!res.ok) {
          // When upload fails before task is created, we mock a failed task.
          // This ensures that the user error notification will be shown.
          this.failedTasks = [
            ...this.failedTasks,
            {
              meta: { statement_name: file.name },
            },
          ];

          // Send email before throwing error
          const template = res.status === 403 ? 'upload_403' : 'upload';
          postSendInternalEmail(`portfolio_checkup/error/${template}.yaml`);

          // Throw an error with the server response details
          // Should get picked up by Sentry
          const responseBody = await res.text();
          const errorContext = {
            status: res.status,
            statusText: res.statusText,
            responseBody,
          };
          throw new Error('Server error while trying to upload statement: ' + JSON.stringify(errorContext));
        }

        // Get the task id from the response
        const data = await res.json();
        if (!data.task_ids) return;

        // TODO: Could use some error handling
        const ids = Object.values(data.task_ids);
        this.taskIDs = { ...this.taskIDs, [file.name]: ids[0] };
      },

  
      async checkTaskStatus() {
        // Checks if the background task is done yet
        // Gets results if they are ready
  
        // { task_name: task_id }
        // This has been refactored so everything is handled by a single task
        // So taskIDs should always be an array of one task id
  
        const taskIDs = Object.values(this.taskIDs).filter(taskID => !!taskID);
        if (!taskIDs.length) return; // This should probably throw an error
  
        const url = `/api/v1/tasks/?task_ids=${taskIDs.join(',')}`;
        const res = await fetch(url, {
          method: 'GET',
          headers: this.headers,
        });
  
        const tasks = (await res.json()).tasks;
  
        const incompleteTasks = tasks.filter(task => {
          if (task.status === 'failed') this.handleFailedTask(task);
          this.updateStatusDetails(task);
          return task.status !== 'complete' && task.status !== 'failed';
        });
        console.log('INCOMPLETE TASKS', incompleteTasks);
        if (!incompleteTasks.length) {
          clearInterval(this.taskCheckInterval);
          this.handleTasksComplete(tasks);
        }
      },
  
      updateStatusDetails(task) {
        if (!task.meta.status) return;
        const status = task.status || 'unknown';
  
        const spinner = 'fa-spinner fa-spin';
  
        const icons = {
          'pending': spinner,
          'failed': 'fa-exclamation-triangle',
          'complete': 'fa-check',
        };
  
        const details = {
          ...task.meta.status,
          status: task.status,
          icon: `fa ${icons[status] || spinner}`,
        };
  
        this.statusData = {
          ...this.statusData,
          [task.id]: details,
        };
      },
  
      handleFailedTask(task) {
        // clearInterval(this.taskCheckInterval);
        console.error('Background task failed', task);
  
        const error = task.meta.error;
        console.error(error.type + ': ' + error.message);
        console.log(error.traceback);
  
        // this.isUploading = false;
        this.taskFailed = true;
  
        if (!this.failedTasks.find(failedTask => failedTask.id === task.id)) {
          this.failedTasks = [...this.failedTasks, task];
        }
      },

      async handleTasksComplete(tasks) {
        for (const task of tasks) {
          if (task.status === 'complete') {
            const result = await this.getTaskResult(task.id);
            if (result) {
              this.storeTaskResult(task.id, result);
            }
          }
        }

        // Process and create a spreadsheet once all tasks are complete
        this.processResults();
      },

      async getTaskResult(taskId) {
        //const url = `/api/v1/onboarding_extraction/results/${taskId}`;
        const url = `/api/v1/account_information_extraction/results/${taskId}`;
        try {
          const res = await fetch(url, {
            method: 'GET',
            headers: this.headers,
          });

          if (!res.ok) {
            console.error(`Error fetching result for task ${taskId}: ${res.status} ${res.statusText}`);
            return null;
          }

          const responseText = await res.text();

          try {
            const result = JSON.parse(responseText);
            return result;
          } catch (jsonError) {
            console.error(`Failed to parse JSON for task ${taskId}:`, jsonError);
            return null;
          }
        } catch (error) {
          console.error(`Error fetching result for task ${taskId}:`, error);
          return null;
        }
      },

      storeTaskResult(taskId, result) {
        if (result && result.account_onboarding) {
            // Extract all items
            const data = Array.isArray(result.account_onboarding) ? result.account_onboarding : [result.account_onboarding];

            this.$set(this.taskResults, taskId, data);
        }
      },

      async processResults() {
        const allResults = Object.values(this.taskResults).flat();

        console.log('All task results:', allResults);

        const workbook = await this.createResultsWorksheet(allResults);
        const url = await getWorkbookURL(workbook);
        this.isUploading = false; 

        const filename = 'Account Onboarding Information';
        const csvFile = { filename, url };
        this.csvFiles = [csvFile];

      },

      createResultsWorksheet(allResults) {
        const workbook = newWorkbook();
        const worksheet = workbook.addWorksheet('Results');

        const columns = [
            { header: 'Document Name', key: 'document_name', width: 50 },
            { header: 'Client Name', key: 'client_name', width: 30 },
            { header: 'Custodian', key: 'custodian', width: 30 },
            { header: 'Street', key: 'address_street', width: 30 },
            { header: 'City', key: 'address_city', width: 20 },
            { header: 'State', key: 'address_state', width: 10 },
            { header: 'Zip', key: 'address_zip', width: 15 },
            { header: 'Account Number', key: 'account_number', width: 30 },
            { header: 'Account Name', key: 'account_name', width: 50 },
            { header: 'Registration', key: 'registration', width: 30 },
            { header: 'Account Value', key: 'account_value', width: 20 },
            { header: 'Low Confidence Name', key: 'low_confidence_name', width: 30 },
            { header: 'Low Confidence Custodian', key: 'low_confidence_custodian', width: 30 },
            { header: 'Low Confidence Accounts', key: 'low_confidence_accounts', width: 30 },
            { header: 'Low Confidence Address', key: 'low_confidence_address', width: 30 }
        ];

        worksheet.columns = columns;

        allResults.forEach(data => {
            // Check if data is an object before adding it as a row
            if (typeof data === 'object' && data !== null) {
                worksheet.addRow({
                    document_name: data.document_name,
                    client_name: data.client_name,
                    custodian: data.custodian,
                    address_street: data.address_street,
                    address_city: data.address_city,
                    address_state: data.address_state,
                    address_zip: data.address_zip,
                    account_number: data.account_number,
                    account_name: data.account_name,
                    registration: data.registration,
                    account_value: data.account_value,
                    low_confidence_name: data.low_confidence_name,
                    low_confidence_custodian: data.low_confidence_custodian,
                    low_confidence_accounts: data.low_confidence_accounts,
                    low_confidence_address: data.low_confidence_address
                });
            } else {
                console.error('Invalid data entry:', data);
            }
        });

        return workbook;
      },
    }
}
  
  </script>
  
  
  <style scoped>
  
  </style>

<style>
.custom-upload-button a {
  display: inline-block;
  padding: 10px 20px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #f9f9f9;
  text-decoration: none;
  color: #333;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s, border-color 0.3s;
}

.custom-upload-button a:hover {
  background-color: #e6e6e6;
  border-color: #888;
}
</style>
  